import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col, Tooltip } from 'antd';
import CompanyDataCard, { CompanyDataRow } from 'components/reusable/CompanyDataCard';
import { ReactComponent as BasicInfoIcon } from 'assets/img/icons/cardIcons/basicInfo.svg';
import useFormatDate, { DATE_FORMAT_LONG, DATE_FORMAT_YEAR_ONLY } from 'hooks/format/useFormatDate';
import { summaryInformation } from 'store/entities/companySearchResult/selectors/unregisteredReportSelector';

const BasicInfo = () => {
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const { summaryInformation: summaryInformationState } = useSelector(summaryInformation);

  function parseNace2String(nace2String) {
    if (!nace2String) {
      return null;
    }

    const parts = nace2String.split(' - ');

    const code = parts[0].trim();
    const explanation = parts.slice(1).join(' - ').trim();

    return {
      code,
      explanation,
    };
  }

  const nace2Object = parseNace2String(summaryInformationState?.nace2);
  const nace21bject = parseNace2String(summaryInformationState?.nace2_1);
  const concatBasicInfoRowValues = () => {
    const rows = [
      {
        type: t('companyResult.summary.registrationDate'),
        value: summaryInformationState?.registrationDate
          ? formatDate(summaryInformationState?.registrationDate, DATE_FORMAT_LONG)
          : '-',
      },
      {
        type: t('companyResult.summary.companyCode'),
        value: summaryInformationState?.companyCode ? summaryInformationState.companyCode : '-',
      },
      {
        type: t('companyResult.summary.vatCode'),
        value: summaryInformationState?.vat ? summaryInformationState.vat : '-',
      },
      nace21bject?.code
        ? {
            type: t('companyResult.summary.activities2.1'),
            value: nace21bject?.code ?? '-',
          }
        : {
            type: t('companyResult.summary.activities'),
            value: nace2Object?.code ? nace2Object.code : '-',
          },
      {
        type: t('companyResult.summary.pressReleasesTotalCount'),
        value:
          summaryInformationState?.pressReleasesTotalCount?.businessNewsTotalCount +
            summaryInformationState?.pressReleasesTotalCount?.pressReleasesTotalCount || '-',
      },
      {
        type: t('companyResult.summary.vehiclesTotalCount'),
        value: summaryInformationState?.vehiclesTotalCount?.count
          ? summaryInformationState.vehiclesTotalCount.count
          : '-',
      },
    ];

    return rows.map(row => `${row.type} - ${row.value}`).join('\r\n');
  };

  return (
    <Col
      xs={{ span: 24, order: 1 }}
      sm={{ span: 24, order: 1 }}
      md={{ span: 24, order: 1 }}
      lg={{ span: 12, order: 1 }}
      xl={{ span: 12, order: 1 }}
      xxl={{ span: 12, order: 1 }}
      className="flex"
    >
      <CompanyDataCard
        title={t('companyResult.basicInfo.title')}
        icon={<BasicInfoIcon className="title-icon" />}
        copyData={concatBasicInfoRowValues()}
      >
        <div>
          <CompanyDataRow
            halfBlock
            type={t('companyResult.summary.registrationDate')}
            value={
              summaryInformationState?.registrationDate
                ? formatDate(summaryInformationState?.registrationDate, DATE_FORMAT_LONG)
                : '-'
            }
          />
          <CompanyDataRow
            halfBlock
            type={t('companyResult.summary.companyCode')}
            value={summaryInformationState?.companyCode ? summaryInformationState?.companyCode : '-'}
          />
          <CompanyDataRow
            halfBlock
            type={t('companyResult.summary.vatCode')}
            value={summaryInformationState?.vat ? summaryInformationState.vat : '-'}
          />
          {!nace21bject ? (
            <CompanyDataRow
              halfBlock
              type={t('companyResult.summary.activities')}
              value={
                nace2Object?.code && nace2Object.explanation ? (
                  <Tooltip
                    placement="top"
                    title={<span className="justify-content-center">{nace2Object.explanation}</span>}
                  >
                    <span className="underlined">{nace2Object.code}</span>
                  </Tooltip>
                ) : (
                  nace2Object?.code || '-'
                )
              }
            />
          ) : (
            <CompanyDataRow
              halfBlock
              type={t('companyResult.summary.activities2.1')}
              value={
                nace21bject?.code && nace21bject.explanation ? (
                  <Tooltip
                    placement="top"
                    title={<span className="justify-content-center">{nace21bject.explanation}</span>}
                  >
                    <span className="underlined">{nace21bject.code}</span>
                  </Tooltip>
                ) : (
                  nace21bject?.code || '-'
                )
              }
            />
          )}
          <CompanyDataRow
            halfBlock
            type={t('companyResult.summary.pressReleasesTotalCount')}
            value={
              summaryInformationState?.pressReleasesTotalCount?.businessNewsTotalCount +
                summaryInformationState?.pressReleasesTotalCount?.pressReleasesTotalCount || '-'
            }
          />
          <CompanyDataRow
            halfBlock
            type={t('companyResult.summary.vehiclesTotalCount')}
            value={
              summaryInformationState?.vehiclesTotalCount?.count
                ? summaryInformationState.vehiclesTotalCount.count
                : '-'
            }
          />
        </div>
      </CompanyDataCard>
    </Col>
  );
};

export default BasicInfo;
